import React, { createContext, useContext, useState, useEffect } from "react";
import { Position } from "../../interfaces";
import { getRemainingCount } from "../../utils";

interface PositionContextType {
  positions: Position[];
  setPositions: (positions: Position[]) => void;
  allPositionsFilled: boolean;
  recalculatePositionsFilled: () => void;
}

const PositionContext = createContext<PositionContextType | undefined>(
  undefined
);

export const usePosition = (): PositionContextType => {
  const context = useContext(PositionContext);
  if (!context) {
    throw new Error("usePosition must be used within a PositionProvider");
  }
  return context;
};

export const PositionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [positions, setPositionsState] = useState<Position[]>([]);
  const [allPositionsFilled, setAllPositionsFilled] = useState<boolean>(false);

  const setPositions = (newPositions: Position[]) => {
    setPositionsState([...newPositions]); // Ensure a new array is always passed
  };

  const recalculatePositionsFilled = () => {
    const allFilled = positions.every((position) => {
      const remainingCount = getRemainingCount(position);
      return remainingCount === 0;
    });
    setAllPositionsFilled(allFilled);
  };

  useEffect(() => {
    recalculatePositionsFilled(); // Recalculate when positions change
  }, [positions]);

  return (
    <PositionContext.Provider
      value={{
        positions,
        setPositions,
        allPositionsFilled,
        recalculatePositionsFilled,
      }}
    >
      {children}
    </PositionContext.Provider>
  );
};
